import { localizedStrings } from '@core/strings'
import React, { useMemo, useRef } from 'react'
import { TextField } from '@common/material'
import { Box, Flex, Text } from '@fundamentals'
import { useGetRegisterItems } from '@core/react-query/features/registers/registers/register/items/hooks/useGetRegisterItems'
import { Section } from '@common/Section'
import { useGetRegisterItem } from '@core/react-query/features/registers/registers/register/items/item'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { useToast } from '@core/toast'
import { AutocompleteProps } from '@mui/material'
import { RegisterItemPropertyValue } from '@modules/registers/propertyValue/RegisterItemPropertyValue'
import { Autocomplete } from '@common/material/Autocomplete'
import useConstants from '@core/providers/useConstants'

type RegisterItemPickerProps = Partial<
  AutocompleteProps<any, any, any, any>
> & {
  registerId: number
  readOnly?: boolean
  onChange?: (itemId: number) => void
  error?: boolean
  fromInspection?: boolean
  helperText?: string
}

const RegisterItemPicker: React.FC<RegisterItemPickerProps> = ({
  registerId,
  readOnly,
  error,
  value,
  fromInspection,
  onChange,
  ...props
}) => {
  const { constants } = useConstants()
  const toast = useToast()
  const [registerItemCache, setRegisterItemCache] = React.useState<
    Record<number, RegisterItem>
  >({})
  const [searchTerm, setSearchTerm] = React.useState('')
  const pickerInputRef = useRef(null)

  const { data: registerItemList } = useGetRegisterItems(
    { registerId, term: searchTerm.trim() },
    {
      onSuccess: (data) => {
        const allContent = data?.pages.flatMap((page) => page.content)
        const newCache = allContent.reduce((acc, current) => {
          acc[current.id] = current
          return acc
        }, {} as Record<number, RegisterItem>)
        setRegisterItemCache((cache) => ({
          ...cache,
          ...newCache,
        }))
      },
      refetchOnMount: !readOnly,
    },
  )
  const { data: registerItemById } = useGetRegisterItem(
    {
      registerId,
      registerItemId: value as number,
    },
    {
      onSuccess: (data) => {
        setRegisterItemCache((cache) => ({
          ...cache,
          [data.id]: data,
        }))
      },
      onError: (error) => toast.error(error.message),
      enabled: !!value && !readOnly,
      refetchOnMount: !readOnly,
    },
  )

  const registerItem = registerItemCache[value as number] || registerItemById

  if (readOnly && !registerItem) return

  if (readOnly) {
    const itemData = registerItem.getRegisterItemPreviewData(
      constants.max_register_card_properties,
    )
    return (
      <Section
        sx={props?.sx}
        label={localizedStrings.registerItem}
        width='100%'
      >
        {itemData.map((registerItemProperty) => {
          return (
            <Text key={registerItemProperty.registerPropertyId}>{`${
              registerItemProperty.name
            }: ${registerItem.getRegisterItemPropertyDisplayValues(
              registerItemProperty,
            )}`}</Text>
          )
        })}
      </Section>
    )
  }

  const registerItemsContent = useMemo(() => {
    const items = registerItemList?.pages.flatMap((page) => page.content) || []
    // If a value is pre-selected, and it's not in the list, add it to the end.
    if (value && !items.some((item) => item.id === value) && registerItem) {
      return [...items, registerItem]
    }
    return items
  }, [registerItemList?.pages])

  const registerItemPreview = (item: RegisterItem) => {
    const itemData = item.getRegisterItemPreviewData(
      constants.max_register_card_properties,
    )

    return (
      <Box sx={{ maxWidth: '100%' }}>
        {itemData.map((registerItemProperty, index) => {
          return (
            <Flex key={index} alignItems='center'>
              <Box>
                <Text fontSize={14} sx={{ pr: 0.5 }} color='text.secondary'>
                  {registerItemProperty.name}:
                </Text>
              </Box>
              <Box>
                <RegisterItemPropertyValue
                  variant={'picker'}
                  registerItemPropertyData={registerItemProperty}
                />
              </Box>
            </Flex>
          )
        })}
      </Box>
    )
  }

  return (
    <Autocomplete
      options={registerItemsContent?.map((item) => item.id)}
      renderOption={(optionProps, option) => {
        const index = props['data-option-index']
        return (
          <Box
            key={props.id}
            data-test={`select-register-item-dropdown-option-${index}`}
            sx={{
              borderBottom:
                index !== registerItemsContent.length - 1 &&
                '1px solid #e0e0e0',
            }}
            {...(optionProps as any)}
          >
            {registerItemPreview(
              registerItemsContent?.find((item) => item.id === option),
            )}
          </Box>
        )
      }}
      renderInput={({ InputProps, ...params }) => {
        const selectedItem = registerItemsContent?.find(
          (item) => item.id == value,
        )
        return (
          <TextField
            ref={pickerInputRef}
            sx={
              selectedItem && {
                '& input': { height: 0, padding: '0 !important' },
              }
            }
            InputProps={{
              ...InputProps,
              startAdornment: selectedItem && (
                <Box
                  onMouseDown={(args) => {
                    if (props?.disabled) return

                    params.inputProps.onMouseDown(args as any)
                    pickerInputRef.current.focus()
                  }}
                  sx={{ p: 1, maxWidth: '100%' }}
                >
                  {registerItemPreview(selectedItem)}
                </Box>
              ),
            }}
            label={localizedStrings.registerItem}
            data-test='select-register-item-dropdown-btn'
            FormHelperTextProps={{ error, sx: { pt: 0.5, pb: 2 } }}
            required
            {...params}
          />
        )
      }}
      filterOptions={(x) => x}
      getOptionLabel={(option) => `${option}`}
      onInputChange={(_, newInputValue, reason) => {
        if (reason !== 'reset') setSearchTerm(newInputValue)
      }}
      inputValue={searchTerm}
      onChange={(_, newValue) => {
        onChange(newValue)
        setSearchTerm(' ')
      }}
      {...props}
    />
  )
}

export { RegisterItemPicker }
